// Configuration ("primordial data", 03 §3.2–3.3, 12 §12.3–12.4, 13 §13.4, 14 §14.7).
// Written only through the `saveConfig` callable (04 §4.7): validated here, permission-checked,
// versioned and audited on the server. Clients read these collections within their scope.
import { z } from 'zod';
import {
  CURRENCY_CODES,
  LABOUR_LAWS,
  LEAVE_REASONS,
  PARTNER_STATUSES,
  ROUTE_STATUSES,
  SERVICE_LINES,
  handlingAllowedFor,
  lineAllowedFor,
  routeId,
  seriesFormatProblems,
  type Permission,
} from '@ecobox/domain';
import { campaignSchema, reviewLinkSchema, rewardsRulesSchema } from './customerConfig';

export const currencySchema = z.enum(CURRENCY_CODES as unknown as [string, ...string[]]);
const minor = z.number().int().nonnegative().max(1e12);
const isoDate = z.iso.date();
const nonEmpty = (max = 120) => z.string().trim().min(1).max(max);

export const sectorCodeSchema = z.string().regex(/^[A-Z]{2,4}$/, '2–4 capital letters, e.g. KSA');
export const branchCodeSchema = z.string().regex(/^[A-Z]{3,4}$/, '3–4 capital letters, e.g. RUH');
export const slugSchema = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lower case letters, digits and dashes')
  .max(80);
export const sizeCodeSchema = z
  .string()
  .regex(/^[A-Z0-9]{1,6}$/, 'Up to 6 capital letters or digits');

/** Days of the week, 0 = Sunday … 6 = Saturday. */
const weekdaySchema = z.number().int().min(0).max(6);

// ---- Geography --------------------------------------------------------------

// ---- Legal details and e-invoicing (06 §6.8.7) -------------------------------------
// The issuing entity's legal identity and its tax-authority settings. Secrets are never stored
// here: the Secret Manager names are derived from `zatca.secretPrefix` and read by the server.

/** The countries that have an invoice profile in `@ecobox/einvoice` (kept in step by a test there). */
export const EINVOICE_COUNTRY_CODES = ['KSA', 'UAE', 'QA', 'PH', 'IN'] as const;
export type EInvoiceCountryCode = (typeof EINVOICE_COUNTRY_CODES)[number];

/** A structured postal address; KSA also needs the national address (building, additional number, district). */
export const legalAddressSchema = z
  .object({
    street: nonEmpty(200),
    additional: z.string().max(200).optional(),
    /** KSA national address: 4 digits. */
    buildingNumber: z.string().max(10).optional(),
    /** KSA national address: 4 digits. */
    additionalNumber: z.string().max(10).optional(),
    district: z.string().max(100).optional(),
    city: nonEmpty(100),
    /** The emirate (UAE), region or province. */
    region: z.string().max(100).optional(),
    postalCode: z.string().max(12).optional(),
    /** ISO 3166-1 alpha-2, e.g. SA. */
    countryCode: z.string().regex(/^[A-Z]{2}$/, 'Two capital letters, e.g. SA'),
    /** KSA: the short address of the national address, e.g. RRRD2929 (the ZATCA CSR carries it). */
    shortAddress: z
      .string()
      .regex(/^[A-Z]{4}\d{4}$/, 'Four capital letters and four digits, e.g. RRRD2929')
      .optional(),
  })
  .strict();

/** `sectors/{code}.legal`: who issues the invoice. */
export const legalDetailsSchema = z
  .object({
    name: nonEmpty(200),
    /** Required in KSA: the registered name in Arabic, as on the CR. */
    nameAr: z.string().trim().max(200).optional(),
    tradeName: z.string().trim().max(200).optional(),
    tradeNameAr: z.string().trim().max(200).optional(),
    /** KSA: 15-digit VAT number. UAE: the 15-digit TRN. PH: the TIN. */
    vatNumber: z.string().trim().max(30).optional(),
    /** KSA: the 10-digit CR. UAE: the trade licence number. QA: the CR. */
    crNumber: z.string().trim().max(30).optional(),
    address: legalAddressSchema,
    addressAr: z.string().max(300).optional(),
    phone: z.string().max(30).optional(),
    email: z.string().max(120).optional(),
    /** True for the seeded placeholder details. Production e-invoicing refuses sample details. */
    isSample: z.boolean().default(false),
  })
  .strict();
export type LegalDetails = z.infer<typeof legalDetailsSchema>;

export const EINVOICING_MODES = ['off', 'simulation', 'production'] as const;
export type EInvoicingMode = (typeof EINVOICING_MODES)[number];
export const INVOICE_ISSUE_ON = ['collection', 'payment'] as const;
export type InvoiceIssueOn = (typeof INVOICE_ISSUE_ON)[number];

const secretPrefixSchema = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_]{1,60}$/, 'Letters, digits and underscores, e.g. ZATCA_KSA');

/** `sectors/{code}.einvoicing`: how the entity issues and reports invoices. */
export const einvoicingSchema = z
  .object({
    /**
     * Which country's invoice profile applies. Defaults to the sector code, which is right for the
     * real countries; test sectors (TKSA) name the profile they imitate.
     */
    country: z.enum(EINVOICE_COUNTRY_CODES).optional(),
    /** `off`: no invoice is issued. `simulation`: ZATCA's simulation portal (or a fake). `production`: live. */
    mode: z.enum(EINVOICING_MODES).default('off'),
    /** When the invoice is issued: at collection (default; the date of supply) or when paid in full. */
    issueOn: z.enum(INVOICE_ISSUE_ON).default('collection'),
    zatca: z
      .object({
        /** The EGS unit: one server-side unit per KSA entity to start. Its hash chain is `zatcaChains/{egsUnitId}`. */
        egsUnitId: z.string().regex(/^[A-Za-z0-9_-]{3,60}$/),
        /** 1000 standard, 0100 simplified, 1100 both. */
        invoiceTypes: z.enum(['1000', '0100', '1100']).default('1100'),
        /**
         * The Secret Manager names are `{prefix}_KEY`, `{prefix}_CSID` and `{prefix}_SECRET`
         * (the private key, the certificate and the CSID secret). Never the values.
         */
        secretPrefix: secretPrefixSchema,
        /** The CSR's business category. Default: Transportation and storage. */
        businessCategory: z.string().max(100).optional(),
        /** Local and test only: a different Fatoora endpoint (a fake server). Production ignores it. */
        baseUrl: z.string().url().max(300).optional(),
        /** Set by the onboarding wizard, never typed. */
        csid: z
          .object({
            environment: z.enum(['sandbox', 'simulation', 'production']),
            requestId: z.string().max(60).optional(),
            issuedAt: z.string().optional(),
            expiresAt: z.string().optional(),
            onboardedAt: z.string(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    uae: z
      .object({
        /** True once e-invoicing applies to the entity: B2B documents also go out as PINT AE. */
        eInvoicing: z.boolean().default(false),
        peppolId: z.string().max(60).optional(),
        aspProvider: z.string().max(60).optional(),
        /** The Secret Manager name of the provider's API key. */
        aspSecretName: secretPrefixSchema.optional(),
      })
      .strict()
      .optional(),
    bir: z
      .object({
        tin: z.string().max(30),
        vatRegistered: z.boolean(),
        businessStyle: z.string().max(120).optional(),
        atp: z
          .object({
            number: z.string().max(40),
            dateIssued: isoDate,
            serialFrom: z.string().max(30),
            serialTo: z.string().max(30),
          })
          .strict()
          .optional(),
        cas: z
          .object({ permitNo: z.string().max(40), dateIssued: isoDate })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type EInvoicingSettings = z.infer<typeof einvoicingSchema>;

/** The Secret Manager names for an EGS unit, derived from the settings (never stored as values). */
export function zatcaSecretNames(prefix: string): { key: string; csid: string; secret: string } {
  return { key: `${prefix}_KEY`, csid: `${prefix}_CSID`, secret: `${prefix}_SECRET` };
}

/** `sectors/{code}`: a country operation; operating countries are also accounting entities. */
export const sectorSchema = z
  .object({
    code: sectorCodeSchema,
    name: nonEmpty(),
    type: z.enum(['origin', 'destination']),
    currency: currencySchema,
    timezone: nonEmpty(60),
    vatRatePercent: z.number().min(0).max(30),
    /** D-18: prices include VAT. Kept as a field so a country could differ later. */
    pricesIncludeVat: z.boolean().default(true),
    weekend: z.array(weekdaySchema).max(3).default([5, 6]),
    /** False for a destination served only by Prime couriers (India, D-25). */
    isEntity: z.boolean().default(true),
    active: z.boolean().default(true),
    /** Who issues the invoice (06 §6.8.7). Edited through `saveEinvoiceSettings`, not the country form. */
    legal: legalDetailsSchema.optional(),
    /** How invoices are issued and reported (06 §6.8.7). */
    einvoicing: einvoicingSchema.optional(),
  })
  .strict();

/** `branches/{code}` */
export const branchSchema = z
  .object({
    code: branchCodeSchema,
    name: nonEmpty(),
    sectorId: sectorCodeSchema,
    type: z.enum(['hub', 'satellite']),
    parentHubId: branchCodeSchema.optional(),
    isSectorHq: z.boolean().default(false),
    address: z.string().max(300).default(''),
    phone: z.string().max(30).default(''),
    /** The town or city for the website's branch list (the address line has the street). Public. */
    city: z.string().trim().max(80).optional(),
    /** WhatsApp number in international format. Shown on the website only when set. Public. */
    whatsapp: z.string().max(30).optional(),
    /** Opening hours as plain text, e.g. "Sat–Thu 09:00–21:00". Shown on the website. Public. */
    openingHours: z.string().trim().max(200).optional(),
    /** Where the branch is, for maps and for the suggested order of a run (nearest first). */
    geo: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .strict()
      .optional(),
    active: z.boolean().default(true),
  })
  .strict();

/** `ports/{code}` (UN/LOCODE, e.g. PHMNL) */
export const portSchema = z
  .object({
    code: z.string().regex(/^[A-Z]{5}$/, 'UN/LOCODE, 5 capital letters'),
    name: nonEmpty(),
    kind: z.enum(['sea', 'air']),
    sectorId: sectorCodeSchema,
    linkedHubId: branchCodeSchema.optional(),
    holidays: z.array(isoDate).max(60).default([]),
    /** Where the port is, for the network map. */
    geo: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .strict()
      .optional(),
  })
  .strict();

/** `localZones/{id}` */
export const localZoneSchema = z
  .object({
    id: slugSchema,
    sectorId: sectorCodeSchema,
    name: nonEmpty(),
    servingBranchId: branchCodeSchema.optional(),
  })
  .strict();

/** `destinationCities/{id}` */
export const destinationCitySchema = z
  .object({
    id: slugSchema,
    country: sectorCodeSchema,
    province: z.string().max(80).default(''),
    region: z.string().max(80).default(''),
    city: nonEmpty(80),
    displayName: nonEmpty(),
    /** The pricing route: what rate cards match on (MNL, BUL, CEB, ILO, DVO in the Philippines). */
    routeCode: z.string().max(20).default(''),
    zoneId: slugSchema.optional(),
    aliases: z.array(z.string().max(80)).max(20).default([]),
    /**
     * The delivery route a driver or partner runs (the owner's Philippines location list, e.g.
     * "North Cavite", "Bohol"): for planning and runs, never for prices.
     */
    deliveryRoute: z.string().trim().max(60).default(''),
    /** Philippines: Luzon, Visayas or Mindanao, as the owner's list classes the city. */
    islandGroup: z.enum(['Luzon', 'Visayas', 'Mindanao']).optional(),
    /** The branch that served the city before 2020 (the owner's list), kept for planning only. */
    legacyBranch: z.string().trim().max(40).default(''),
    active: z.boolean().default(true),
  })
  .strict();

// ---- Boxes and prices -------------------------------------------------------

/** `boxSizes/{code}` */
export const boxSizeSchema = z
  .object({
    code: sizeCodeSchema,
    name: nonEmpty(60),
    kind: z.enum(['standard', 'odd']).default('standard'),
    dimsCm: z
      .object({ l: z.number().positive(), w: z.number().positive(), h: z.number().positive() })
      .strict()
      .optional(),
    maxWeightKg: z.number().positive().max(500).optional(),
    /** Default weight used only for quotes before the box is weighed (03 §3.3). */
    quoteWeightKg: z.number().positive().max(500).optional(),
    enabledBranchIds: z.array(branchCodeSchema).max(200).default([]),
    sortOrder: z.number().int().min(0).max(999).default(0),
    active: z.boolean().default(true),
  })
  .strict();

/**
 * The HANDLING MODE: how cargo physically moves (container, air consolidation, courier). Containers,
 * handovers, legs, reserves, SLAs and cost models read it. The product a customer buys is the
 * service line below (D-28).
 */
export const SERVICES = ['sea', 'air', 'prime'] as const;
export type Service = (typeof SERVICES)[number];

/** The product the customer buys (docs/EcoBox-Service-Lines.md). Optional on every document that carries `service`. */
export { SERVICE_LINES, type ServiceLine } from '@ecobox/domain';
export const serviceLineSchema = z.enum(SERVICE_LINES);

/**
 * `routes/{ORIGIN}__{DEST}__{LINE}` (03 §3.2, D-28): whether a service line is offered from an
 * origin country to a destination country, and how its cargo moves. `coming_soon` is the default
 * for anything not confirmed; `open` is the owner's setting once the licence, prices and a sailing
 * or flight date are in place. Balikbayan lines go to the Philippines only, Family Box never.
 */
export const routeSchema = z
  .object({
    id: z.string().min(1).max(60),
    /** The origin country, by sector code (UAE, KSA, QA). */
    origin: sectorCodeSchema,
    /** ISO 3166-1 alpha-2 (PH, IN, NP, MY, ID …); the same code as a destination city's `country`. */
    destinationCountry: z.string().regex(/^[A-Z]{2,4}$/, '2–4 capital letters, e.g. PH'),
    serviceLine: serviceLineSchema,
    status: z.enum(ROUTE_STATUSES),
    /** How this route's cargo moves, e.g. Family Box Air to India is `prime` (a courier) today. */
    handling: z.enum(SERVICES),
    partnerId: z.string().min(1).max(60).optional(),
    rateCardId: z.string().min(1).max(60).optional(),
    nextDeparture: isoDate.optional(),
    note: z.string().max(300).optional(),
  })
  .strict()
  .superRefine((r, ctx) => {
    if (!lineAllowedFor(r.serviceLine, r.destinationCountry)) {
      ctx.addIssue({
        code: 'custom',
        path: ['serviceLine'],
        message:
          r.destinationCountry === 'PH'
            ? 'Family Box is never offered to the Philippines: use a Balikbayan line'
            : 'Balikbayan is for the Philippines only: use a Family Box line',
      });
    }
    if (!handlingAllowedFor(r.serviceLine, r.handling)) {
      ctx.addIssue({
        code: 'custom',
        path: ['handling'],
        message: `A ${r.serviceLine} route cannot move its cargo by ${r.handling}: sea lines go by sea, air lines by air or courier, Documents by courier`,
      });
    }
    const expected = routeId(r.origin, r.destinationCountry, r.serviceLine);
    if (r.id !== expected) {
      ctx.addIssue({
        code: 'custom',
        path: ['id'],
        message: `The route id is origin, destination and line joined by __ (${expected})`,
      });
    }
  });
export type RouteRecord = z.infer<typeof routeSchema>;

export const destinationMatchSchema = z
  .object({
    kind: z.enum(['city', 'route', 'zone', 'branch', 'country']),
    value: z.string().min(1).max(80),
  })
  .strict();

/** One pricing rule (03 §3.3). Most specific destination wins, then a size-specific rule. */
export const rateRuleSchema = z
  .object({
    id: z.string().min(1).max(40),
    destination: destinationMatchSchema.optional(),
    sizeId: sizeCodeSchema.optional(),
    basis: z.enum(['per_box', 'per_kg', 'per_cbm']),
    rateMinor: minor,
    minChargeMinor: minor.optional(),
    /** Air and Prime: chargeable kg = max(actual, l×w×h / divisor). */
    volumetricDivisor: z.number().int().positive().max(10000).optional(),
    /**
     * per_kg only: a weight bracket by chargeable kg, `minKg` inclusive, `maxKg` exclusive. "10–29 kg
     * at X, 30 kg and up at Y" is two rules {minKg: 10, maxKg: 30} and {minKg: 30} (03 §3.3).
     */
    minKg: z.number().nonnegative().max(10000).optional(),
    maxKg: z.number().positive().max(10000).optional(),
    /** per_kg only: the weight charged at least ("minimum 10 kg"). */
    minChargeableKg: z.number().positive().max(10000).optional(),
  })
  .strict()
  .refine(
    (r) =>
      r.basis === 'per_kg' ||
      (r.minKg === undefined && r.maxKg === undefined && r.minChargeableKg === undefined),
    { message: 'Weight brackets and a minimum weight are for per-kg rules only', path: ['basis'] },
  )
  .refine((r) => r.minKg === undefined || r.maxKg === undefined || r.minKg < r.maxKg, {
    message: 'The bracket must start below where it ends (minKg < maxKg)',
    path: ['maxKg'],
  });

export const addOnSchema = z
  .object({
    code: z.string().regex(/^[a-z_]{2,30}$/),
    label: nonEmpty(60),
    kind: z.enum(['percent_of_declared', 'per_shipment', 'per_box']),
    /** Percent (e.g. 1 for 1%) or an amount in minor units. */
    value: z.number().nonnegative().max(1e12),
    minChargeMinor: minor.optional(),
  })
  .strict();

/**
 * `rateCards/{id}`. Never edited once active (D-09): publishing a new version retires the old one.
 * All amounts are VAT-inclusive (D-18).
 */
export const rateCardSchema = z
  .object({
    // `rc_UAE_sea` (a handling mode) or `rc_UAE_bb_sea` (a service line, D-28), then an optional `_vN`.
    id: z
      .string()
      .regex(
        /^rc_[A-Z0-9]{2,12}_(?:[a-z]+|(?:bb|fb)_(?:sea|air))(_v\d+)?$/,
        'rc_{SCOPE}_{service or line}[_vN]',
      ),
    name: nonEmpty(),
    originBranchId: branchCodeSchema.optional(),
    originSectorId: sectorCodeSchema.optional(),
    service: z.enum(SERVICES),
    /** The product this card prices, when set (D-28). Old cards have none. */
    serviceLine: serviceLineSchema.optional(),
    currency: currencySchema,
    effectiveFrom: isoDate,
    effectiveTo: isoDate.optional(),
    status: z.enum(['draft', 'active', 'retired']).default('draft'),
    version: z.number().int().positive().default(1),
    rules: z.array(rateRuleSchema).max(500),
    addOns: z.array(addOnSchema).max(20).default([]),
    minimumChargeMinor: minor.optional(),
  })
  .strict()
  .refine((c) => Boolean(c.originBranchId) !== Boolean(c.originSectorId), {
    message: 'Set either an origin branch or an origin country, not both',
  });

/**
 * `promotions/{id}` (03 §3.3): a date-ranged discount the quote applies by itself, e.g. 15% off sea
 * boxes from 1 to 25 December. Either a percentage or a fixed amount per currency, VAT-inclusive.
 * The rules (what it takes off, one per shipment) are in packages/domain/src/promotions.ts.
 */
export const promotionSchema = z
  .object({
    id: z
      .string()
      .regex(/^prm_[a-z0-9_-]{2,40}$/, 'prm_ and 2–40 lower-case letters, digits, - or _'),
    name: nonEmpty(),
    /** What receipts and the website show; the name when absent. */
    label: z.string().trim().min(1).max(60).optional(),
    status: z.enum(['draft', 'active', 'retired']).default('draft'),
    percentOff: z
      .number()
      .min(0.1)
      .max(90)
      .refine((v) => Math.abs(v * 10 - Math.round(v * 10)) < 1e-9, 'At most one decimal')
      .optional(),
    amountOffMinor: z.record(currencySchema, minor.positive()).optional(),
    /** Inclusive, in the origin country's calendar. */
    startsOn: isoDate,
    endsOn: isoDate,
    originSectorIds: z.array(sectorCodeSchema).max(20).default([]),
    originBranchIds: z.array(branchCodeSchema).max(50).default([]),
    services: z.array(z.enum(SERVICES)).max(3).default([]),
    serviceLines: z.array(serviceLineSchema).max(10).default([]),
    destinationCountries: z.array(sectorCodeSchema).max(20).default([]),
    sizeIds: z.array(sizeCodeSchema).max(20).default([]),
    notes: z.string().max(500).optional(),
  })
  .strict()
  .refine((p) => (p.percentOff === undefined) !== (p.amountOffMinor === undefined), {
    message: 'Set either a percentage or a fixed amount off, not both',
  })
  .refine((p) => p.amountOffMinor === undefined || Object.keys(p.amountOffMinor).length > 0, {
    message: 'Give the fixed amount in at least one currency',
  })
  .refine((p) => p.endsOn >= p.startsOn, { message: 'The end date is before the start date' });

/** `emptyBoxPrices/{branchId}__{sizeId}` */
export const emptyBoxPriceSchema = z
  .object({
    branchId: branchCodeSchema,
    sizeId: sizeCodeSchema,
    priceMinor: minor,
    costMinor: minor,
    currency: currencySchema,
  })
  .strict();

// ---- Branch operations ------------------------------------------------------

/** `branchSettings/{branchId}` */
export const branchSettingsSchema = z
  .object({
    branchId: branchCodeSchema,
    pickupWindows: z
      .array(
        z
          .object({
            from: z.string().regex(/^\d{2}:\d{2}$/),
            to: z.string().regex(/^\d{2}:\d{2}$/),
          })
          .strict(),
      )
      .max(10)
      .default([]),
    collectDestinationChargesOnDelivery: z.boolean().default(true),
    receiptFooter: z.string().max(400).default(''),
  })
  .strict();

/** `vehicles/{id}` */
export const vehicleSchema = z
  .object({
    id: slugSchema,
    branchId: branchCodeSchema,
    plate: nonEmpty(20),
    type: z.enum(['truck', 'van', 'pickup', 'motorcycle', 'car', 'other']),
    capacityBoxes: z.number().int().positive().max(2000).optional(),
    active: z.boolean().default(true),
  })
  .strict();

// ---- Partners and coverage (12) --------------------------------------------

export const PARTNER_RATE_SCOPES = ['city', 'route', 'zone', 'region', 'any'] as const;

const partnerRateRowBase = z
  .object({
    /**
     * What `destination` names (12 §12.4): a city id, a route code, a zone id, a region (province)
     * name, or `any` (destination `*`). Rows saved before 2026-10-26 have none: `*` is everywhere,
     * anything else a city id or a region name.
     */
    appliesTo: z.enum(PARTNER_RATE_SCOPES).optional(),
    destination: z.string().min(1).max(80),
    sizeId: sizeCodeSchema.optional(),
    basis: z.enum(['per_box', 'per_kg']).default('per_box'),
    amountMinor: minor,
    currency: currencySchema,
  })
  .strict();

export const partnerRateRowSchema = partnerRateRowBase.refine(
  (r) => r.appliesTo === undefined || (r.appliesTo === 'any') === (r.destination === '*'),
  {
    message: 'A rate for everywhere (any) has destination *, and only it does',
    path: ['destination'],
  },
);

/** `partners/{id}`: a vendor list. Add a hauler or courier when you start using it. */
export const partnerSchema = z
  .object({
    id: z.string().regex(/^ptn_[A-Z0-9]{2,26}$/, 'ptn_ followed by capitals or digits'),
    name: nonEmpty(),
    type: z.enum([
      'local_hauler',
      'courier_forwarder',
      'freight_forwarder',
      'customs_broker',
      'trucker',
    ]),
    service: z.string().max(120).default(''),
    coverage: z.string().max(300).default(''),
    contacts: z
      .array(
        z
          .object({
            name: nonEmpty(80),
            role: z.string().max(60).default(''),
            whatsapp: z.string().max(30).default(''),
            phone: z.string().max(30).default(''),
            email: z.string().max(120).default(''),
          })
          .strict(),
      )
      .max(10)
      .default([]),
    integration: z.enum(['link', 'manual', 'api']).default('link'),
    /**
     * The adapter for `integration: 'api'` (12 §12.7 channel 3). Secrets live in Secret Manager and
     * are named here, never stored: `secretName` is the Secret Manager id, not the value.
     */
    adapter: z
      .object({
        name: z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lower case letters, digits and dashes'),
        /** `manual-json`: a JSON tracking URL; `{ref}` (one reference) or `{refs}` (comma list) is replaced. */
        trackingUrl: z.string().url().max(500).optional(),
        secretName: z
          .string()
          .regex(/^[A-Za-z0-9_-]{1,255}$/)
          .optional(),
        settings: z
          .record(z.string().max(40), z.union([z.string().max(200), z.number(), z.boolean()]))
          .default({}),
      })
      .strict()
      .optional(),
    /** Their status words (lower case) to our partner statuses (12 §12.5). */
    statusMap: z.record(z.string().min(1).max(60), z.enum(PARTNER_STATUSES)).default({}),
    rates: z.array(partnerRateRowSchema).max(2000).default([]),
    slaDays: z.number().int().positive().max(60).optional(),
    paymentTerms: z.string().max(120).default(''),
    showNameToCustomers: z.boolean().default(false),
    /** Where the partner works from (its depot or office), for the network map. */
    geo: z
      .object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) })
      .strict()
      .optional(),
    active: z.boolean().default(true),
  })
  .strict()
  .refine((p) => p.integration !== 'api' || p.adapter !== undefined, {
    message: 'An API partner needs an adapter',
    path: ['adapter'],
  });

/** `coverage/{cityId}`: who delivers each destination city (12 §12.3). */
export const coverageSchema = z
  .object({
    cityId: slugSchema,
    mode: z.enum(['own', 'partner']),
    servingBranchId: branchCodeSchema,
    partnerId: z
      .string()
      .regex(/^ptn_[A-Z0-9]{2,26}$/)
      .optional(),
    backupPartnerId: z
      .string()
      .regex(/^ptn_[A-Z0-9]{2,26}$/)
      .optional(),
    targetDays: z.number().int().positive().max(60),
  })
  .strict()
  .refine((c) => c.mode === 'own' || Boolean(c.partnerId), {
    message: 'A partner city needs a partner',
  });

// ---- Transport vendors (12 §12.14) -------------------------------------------

export const HIRE_VEHICLES = ['wing_van', 'truck', 'closed_van', 'other'] as const;

/** One vehicle a vendor rents out, and what it costs (domain `vehicle-hire.ts` does the sums). */
export const hireRateSchema = z
  .object({
    vehicle: z.enum(HIRE_VEHICLES),
    label: z.string().trim().max(80).default(''),
    basis: z.enum(['per_day', 'per_hire', 'per_trip']),
    amountMinor: minor,
    currency: currencySchema,
    /** `per_hire`: the days the amount covers. */
    includedDays: z.number().int().positive().max(60).optional(),
    /** `per_hire`: each day after the included days. */
    extraDayMinor: minor.optional(),
    capacityBoxes: z
      .object({
        min: z.number().int().positive().max(5000),
        max: z.number().int().positive().max(5000),
      })
      .strict()
      .refine((c) => c.min <= c.max, { message: 'The low end is above the high end' })
      .optional(),
    /** A one-off quote for a job rather than the vendor's standing price. */
    spot: z.boolean().default(false),
    validUntil: isoDate.optional(),
    notes: z.string().max(200).default(''),
  })
  .strict()
  .refine((r) => r.basis !== 'per_hire' || r.includedDays !== undefined, {
    message: 'Say how many days the hire covers',
    path: ['includedDays'],
  });

/** `transportVendors/{id}`: who rents EcoBox wing vans and trucks in a country, and at what rates. */
export const transportVendorSchema = z
  .object({
    id: slugSchema,
    name: nonEmpty(),
    sectorId: sectorCodeSchema,
    contactName: z.string().max(80).default(''),
    whatsapp: z.string().max(30).default(''),
    phone: z.string().max(30).default(''),
    rates: z.array(hireRateSchema).max(40).default([]),
    notes: z.string().max(400).default(''),
    active: z.boolean().default(true),
  })
  .strict();

// ---- Staff directory (07 §7.6) ---------------------------------------------

export const ALLOWANCE_KINDS = [
  'housing',
  'transport',
  'food',
  'phone',
  'education',
  'other',
] as const;

/**
 * `employees/{id}`: one person EcoBox employs, with their pay, time of service and visa. Readable
 * with `hr.view` (inside the person's country), changed with `hr.manage`, never by an AI worker.
 * Visa, permit, passport and branch are optional: local staff often have none.
 */
export const employeeSchema = z
  .object({
    id: slugSchema,
    fullName: nonEmpty(120),
    /** Their office or ops sign-in, when they have one (`staff/{uid}`). */
    staffUid: z.string().max(128).optional(),
    jobTitle: nonEmpty(80),
    department: z.string().trim().max(60).default(''),
    /** The country that employs them: its labour law and its payroll. */
    sectorId: sectorCodeSchema,
    branchId: branchCodeSchema.optional(),
    labourLaw: z.enum(LABOUR_LAWS),
    employment: z.enum(['local', 'expat']).default('local'),
    nationality: z.string().trim().max(60).default(''),
    contract: z.enum(['unlimited', 'limited']).default('unlimited'),
    startDate: isoDate,
    endDate: isoDate.optional(),
    leaveReason: z.enum(LEAVE_REASONS).optional(),
    salary: z
      .object({
        currency: currencySchema,
        basicMinor: minor,
        allowances: z
          .array(
            z
              .object({
                kind: z.enum(ALLOWANCE_KINDS),
                label: z.string().trim().max(60).default(''),
                amountMinor: minor,
              })
              .strict(),
          )
          .max(20)
          .default([]),
      })
      .strict(),
    visa: z
      .object({
        type: z.string().trim().max(60).default(''),
        sponsor: z.string().trim().max(80).default(''),
        expiresOn: isoDate,
      })
      .strict()
      .optional(),
    workPermitExpiresOn: isoDate.optional(),
    passportExpiresOn: isoDate.optional(),
    notes: z.string().max(500).default(''),
    active: z.boolean().default(true),
  })
  .strict()
  .refine((e) => !e.endDate || e.endDate >= e.startDate, {
    message: 'The last day is before the first day',
    path: ['endDate'],
  })
  .refine((e) => !e.endDate || e.leaveReason !== undefined, {
    message: 'Say why they left',
    path: ['leaveReason'],
  });

// ---- Money planning (14 §14.7) ----------------------------------------------

export const costLineSchema = z
  .object({
    item: z.string().regex(/^[a-z_]{2,30}$/),
    label: nonEmpty(80),
    amountMinor: minor,
    currency: currencySchema,
    payer: z.enum(['origin', 'destination']),
    /** When it falls due, e.g. "ETD+30", "ETA-5", "ATA", "PULLOUT", "HANDOVER+7". */
    dueRule: z.string().regex(/^(ETD|ETA|ATA|PULLOUT|DEVAN|HANDOVER)([+-]\d{1,3})?$/),
    category: z.string().max(40),
  })
  .strict();

export const perBoxCostRowSchema = z
  .object({
    destination: z.string().min(1).max(80),
    sizeId: sizeCodeSchema.optional(),
    forwardingMinor: minor,
    deliveryMinor: minor,
    currency: currencySchema,
    partnerId: z.string().max(40).optional(),
  })
  .strict();

/** `costModels/{origin}__{destination}__{containerType}` */
export const costModelSchema = z
  .object({
    id: z.string().regex(/^[A-Z]{2,4}__[A-Z]{2,4}__[a-z0-9_]{2,20}$/),
    originSectorId: sectorCodeSchema,
    destinationSectorId: sectorCodeSchema,
    containerType: z.enum(['40hc', '40ft', '20ft', 'lcl', 'air']),
    boxesPerContainer: z
      .object({
        planned: z.number().int().positive(),
        min: z.number().int().positive(),
        max: z.number().int().positive(),
      })
      .strict(),
    perContainer: z.array(costLineSchema).max(40),
    /** The per-box table, uploaded later by the owner (14 §14.7 provision). Empty means spread the container delivery cost evenly. */
    perBox: z.array(perBoxCostRowSchema).max(5000).default([]),
    effectiveFrom: isoDate,
    version: z.number().int().positive().default(1),
  })
  .strict();

/**
 * `rechargePolicies/{destination country | GROUP}` (14 §14.9 "policy setting"): how the destination
 * recharges a container's costs to the origin at devanning. The country's document wins over the
 * group's; with neither, the cost-model estimate.
 */
export const rechargePolicySchema = z
  .object({
    id: z.union([z.literal('GROUP'), sectorCodeSchema]),
    basis: z.enum(['cost_model', 'actual']),
  })
  .strict();

// ---- Time (13) --------------------------------------------------------------

/** `seasons/{id}` */
export const seasonSchema = z
  .object({
    id: slugSchema,
    name: nonEmpty(80),
    deliverBy: isoDate,
    colour: z
      .string()
      .regex(/^#[0-9a-fA-F]{6}$/)
      .default('#C62828'),
    autoTag: z.object({ bookingFrom: isoDate, bookingTo: isoDate }).strict().optional(),
    cutoffs: z
      .array(
        z
          .object({
            originSectorId: sectorCodeSchema,
            service: z.enum(SERVICES),
            lastCollectDate: isoDate,
            lastLoadDate: isoDate.optional(),
          })
          .strict(),
      )
      .max(40)
      .default([]),
    active: z.boolean().default(true),
  })
  .strict();

export const CLOCK_KEYS = [
  'origin_detention',
  'origin_consolidation',
  'port_cutoff',
  'transit',
  'bl_release',
  'destination_funding',
  'port_free_time',
  'customs_clearance',
  'empty_return',
  'devanning',
  'waiting_payment',
  'origin_dwell',
  'destination_dwell',
  'partner_leg',
  'no_update',
  'door_to_door',
  'pickup_unscheduled',
  'run_not_started',
  'stop_failed_twice',
  'driver_cash_overnight',
  'exception_open',
  'ticket_open',
  'day_not_closed',
  'einvoice_report',
] as const;

/** `slaPolicies/{id}` */
export const slaPolicySchema = z
  .object({
    id: slugSchema,
    clock: z.enum(CLOCK_KEYS),
    label: nonEmpty(80),
    scope: z
      .object({
        sectorId: sectorCodeSchema.optional(),
        partnerId: z.string().optional(),
        service: z.enum(SERVICES).optional(),
      })
      .strict()
      .default({}),
    target: z
      .object({
        kind: z.enum(['working_days', 'calendar_hours']),
        amount: z.number().positive().max(1000),
      })
      .strict(),
    atRiskAfter: z.number().positive().max(1000).optional(),
    escalateAfter: z.number().positive().max(1000).optional(),
    ownerRole: z.string().max(40),
    escalation: z.array(z.string().max(40)).max(4).default(['branch_manager', 'director']),
  })
  .strict();

// ---- Container capacity and air consolidation (15 §15.1 "Operations snapshot") ----------------

export const SEA_CONTAINER_TYPES = ['20ft', '40ft', '40hc'] as const;
export type SeaContainerType = (typeof SEA_CONTAINER_TYPES)[number];

/**
 * `containerCapacities/{containerType}`: how many balikbayan boxes a container usually takes, as a
 * range (the owner's averages: 160-180 in a 20ft, 285-320 in a 40ft). The container meter shows
 * "enough" at the minimum. Sea cargo only.
 */
export const containerCapacitySchema = z
  .object({
    containerType: z.enum(SEA_CONTAINER_TYPES),
    minBoxes: z.number().int().positive().max(2000),
    maxBoxes: z.number().int().positive().max(2000),
  })
  .strict()
  .refine((c) => c.maxBoxes >= c.minBoxes, {
    message: 'The most boxes cannot be fewer than the least',
    path: ['maxBoxes'],
  });

/**
 * `airConsolidations/{service}`: the weight at which air or Prime cargo is worth sending in one
 * consignment. Without a record the meter shows the weight only.
 */
export const airConsolidationSchema = z
  .object({
    service: z.enum(['air', 'prime']),
    targetKg: z.number().positive().max(100000),
  })
  .strict();

// ---- Numbering (03 §3.5, D-27) ------------------------------------------------

/** A series id: the branch code for a branch's default series, else `nsr_{ULID}`. */
export const numberSeriesIdSchema = z
  .string()
  .regex(/^([A-Z]{3,4}|nsr_[0-9A-HJKMNP-TV-Z]{26})$/, 'A branch code or nsr_…');
export const agentIdSchema = z.string().regex(/^agt_[0-9A-HJKMNP-TV-Z]{26}$/, 'agt_…');

/**
 * `numberSeries/{id}`: how tracking numbers are made, `{PREFIX}{YY}{SEQ}{CHECK}{SUFFIX}`, and who
 * they are for: a branch (its default series, id = the branch code), a customer account or an agent.
 * Saved through `saveConfig` (versioned and audited); two active series that could make the same
 * number, or two for one customer or agent, are refused there.
 */
export const numberSeriesSchema = z
  .object({
    id: numberSeriesIdSchema,
    name: nonEmpty(80),
    prefix: z.string().regex(/^[A-Z0-9-]{0,8}$/, 'A–Z, 0–9 and "-", up to 8'),
    suffix: z
      .string()
      .regex(/^[A-Z0-9-]{0,8}$/, 'A–Z, 0–9 and "-", up to 8')
      .default(''),
    digits: z.number().int().min(4).max(9),
    yearPart: z.boolean(),
    checkDigit: z.boolean(),
    startNumber: z.number().int().min(1).default(1),
    assignedTo: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('branch'), branchId: branchCodeSchema }).strict(),
      z
        .object({
          kind: z.literal('customer'),
          customerId: z.string().regex(/^cus_[0-9A-HJKMNP-TV-Z]{26}$/),
          customerName: z.string().max(120).optional(),
        })
        .strict(),
      z.object({ kind: z.literal('agent'), agentId: agentIdSchema }).strict(),
    ]),
    active: z.boolean().default(true),
  })
  .strict()
  .superRefine((r, ctx) => {
    for (const message of seriesFormatProblems(r)) ctx.addIssue({ code: 'custom', message });
    const to = r.assignedTo;
    if (to.kind === 'branch' && r.id !== to.branchId) {
      ctx.addIssue({
        code: 'custom',
        path: ['id'],
        message: `A branch's default series has the branch code as its id (${to.branchId})`,
      });
    }
    if (to.kind !== 'branch' && !r.id.startsWith('nsr_')) {
      ctx.addIssue({
        code: 'custom',
        path: ['id'],
        message: 'A customer or agent series has an id like nsr_…',
      });
    }
  });
export type NumberSeriesRecord = z.infer<typeof numberSeriesSchema>;

/**
 * `agents/{agt_…}`: someone who brings shipments on EcoBox's behalf (15 lists agents as not built).
 * The smallest record that works: a name, a phone, the branch they bring cargo to. A shipment
 * records the `agentId` when one is chosen; an agent may have their own numbering series.
 */
export const agentSchema = z
  .object({
    id: agentIdSchema,
    name: nonEmpty(80),
    phoneE164: z
      .string()
      .regex(/^\+[1-9]\d{6,14}$/)
      .optional(),
    branchId: branchCodeSchema,
    active: z.boolean().default(true),
  })
  .strict();
export type AgentRecord = z.infer<typeof agentSchema>;

// ---- Registry ---------------------------------------------------------------

export interface ConfigKind<S extends z.ZodType = z.ZodType> {
  collection: string;
  schema: S;
  /** The document id for a record. */
  idOf: (record: z.infer<S>) => string;
  permission: Permission;
  label: string;
}

function kind<S extends z.ZodType>(k: ConfigKind<S>): ConfigKind<S> {
  return k;
}

export const CONFIG_KINDS = {
  sector: kind({
    collection: 'sectors',
    schema: sectorSchema,
    idOf: (r) => r.code,
    permission: 'config.zoning.edit',
    label: 'Country',
  }),
  branch: kind({
    collection: 'branches',
    schema: branchSchema,
    idOf: (r) => r.code,
    permission: 'config.zoning.edit',
    label: 'Branch',
  }),
  port: kind({
    collection: 'ports',
    schema: portSchema,
    idOf: (r) => r.code,
    permission: 'config.zoning.edit',
    label: 'Port',
  }),
  localZone: kind({
    collection: 'localZones',
    schema: localZoneSchema,
    idOf: (r) => r.id,
    permission: 'config.zoning.edit',
    label: 'Local zone',
  }),
  destinationCity: kind({
    collection: 'destinationCities',
    schema: destinationCitySchema,
    idOf: (r) => r.id,
    permission: 'config.zoning.edit',
    label: 'Destination city',
  }),
  boxSize: kind({
    collection: 'boxSizes',
    schema: boxSizeSchema,
    idOf: (r) => r.code,
    permission: 'config.pricing.edit',
    label: 'Box size',
  }),
  rateCard: kind({
    collection: 'rateCards',
    schema: rateCardSchema,
    idOf: (r) => r.id,
    permission: 'config.pricing.edit',
    label: 'Rate card',
  }),
  promotion: kind({
    collection: 'promotions',
    schema: promotionSchema,
    idOf: (r) => r.id,
    permission: 'config.pricing.edit',
    label: 'Promotion',
  }),
  route: kind({
    collection: 'routes',
    schema: routeSchema,
    idOf: (r) => r.id,
    permission: 'config.pricing.edit',
    label: 'Route',
  }),
  emptyBoxPrice: kind({
    collection: 'emptyBoxPrices',
    schema: emptyBoxPriceSchema,
    idOf: (r) => `${r.branchId}__${r.sizeId}`,
    permission: 'config.pricing.edit',
    label: 'Empty box price',
  }),
  branchSettings: kind({
    collection: 'branchSettings',
    schema: branchSettingsSchema,
    idOf: (r) => r.branchId,
    permission: 'config.branch.edit',
    label: 'Branch settings',
  }),
  vehicle: kind({
    collection: 'vehicles',
    schema: vehicleSchema,
    idOf: (r) => r.id,
    permission: 'config.branch.edit',
    label: 'Vehicle',
  }),
  partner: kind({
    collection: 'partners',
    schema: partnerSchema,
    idOf: (r) => r.id,
    permission: 'partner.manage',
    label: 'Partner',
  }),
  coverage: kind({
    collection: 'coverage',
    schema: coverageSchema,
    idOf: (r) => r.cityId,
    permission: 'config.coverage.edit',
    label: 'Coverage',
  }),
  transportVendor: kind({
    collection: 'transportVendors',
    schema: transportVendorSchema,
    idOf: (r) => r.id,
    permission: 'partner.manage',
    label: 'Transport vendor',
  }),
  employee: kind({
    collection: 'employees',
    schema: employeeSchema,
    idOf: (r) => r.id,
    permission: 'hr.manage',
    label: 'Employee',
  }),
  costModel: kind({
    collection: 'costModels',
    schema: costModelSchema,
    idOf: (r) => r.id,
    permission: 'finance.planning',
    label: 'Cost model',
  }),
  rechargePolicy: kind({
    collection: 'rechargePolicies',
    schema: rechargePolicySchema,
    idOf: (r) => r.id,
    permission: 'finance.planning',
    label: 'Recharge policy',
  }),
  season: kind({
    collection: 'seasons',
    schema: seasonSchema,
    idOf: (r) => r.id,
    permission: 'config.time.edit',
    label: 'Season',
  }),
  slaPolicy: kind({
    collection: 'slaPolicies',
    schema: slaPolicySchema,
    idOf: (r) => r.id,
    permission: 'config.time.edit',
    label: 'SLA policy',
  }),
  containerCapacity: kind({
    collection: 'containerCapacities',
    schema: containerCapacitySchema,
    idOf: (r) => r.containerType,
    permission: 'config.branch.edit',
    label: 'Container capacity',
  }),
  airConsolidation: kind({
    collection: 'airConsolidations',
    schema: airConsolidationSchema,
    idOf: (r) => r.service,
    permission: 'config.branch.edit',
    label: 'Air consolidation target',
  }),
  campaign: kind({
    collection: 'campaigns',
    schema: campaignSchema,
    idOf: (r) => r.id,
    permission: 'marketing.manage',
    label: 'Campaign',
  }),
  rewardsRules: kind({
    collection: 'rewardsRules',
    schema: rewardsRulesSchema,
    idOf: (r) => r.id,
    permission: 'rewards.adjust',
    label: 'Points rules',
  }),
  reviewLink: kind({
    collection: 'reviewLinks',
    schema: reviewLinkSchema,
    idOf: (r) => r.sectorId,
    permission: 'marketing.manage',
    label: 'Review links',
  }),
  numberSeries: kind({
    collection: 'numberSeries',
    schema: numberSeriesSchema,
    idOf: (r) => r.id,
    permission: 'config.numbering.edit',
    label: 'Numbering series',
  }),
  agent: kind({
    collection: 'agents',
    schema: agentSchema,
    idOf: (r) => r.id,
    permission: 'config.numbering.edit',
    label: 'Agent',
  }),
} as const;

export type ConfigKindName = keyof typeof CONFIG_KINDS;
export const CONFIG_KIND_NAMES = Object.keys(CONFIG_KINDS) as ConfigKindName[];
export type ConfigRecord<K extends ConfigKindName> = z.infer<(typeof CONFIG_KINDS)[K]['schema']>;

/** Input to the `saveConfig` callable. `delete` deactivates or removes, per kind. */
export const saveConfigInputSchema = z
  .object({
    kind: z.enum(CONFIG_KIND_NAMES as [ConfigKindName, ...ConfigKindName[]]),
    record: z.record(z.string(), z.unknown()),
    /** Optimistic concurrency: the `version` the editor saw, or absent for a new record. */
    expectedVersion: z.number().int().nonnegative().optional(),
  })
  .strict();

export const deleteConfigInputSchema = z
  .object({
    kind: z.enum(CONFIG_KIND_NAMES as [ConfigKindName, ...ConfigKindName[]]),
    id: z.string().min(1).max(120),
  })
  .strict();

/** Fields the server adds to every config document. */
export interface ConfigMeta {
  updatedAt: string;
  updatedBy: string;
  /** Increments on every save; used for optimistic concurrency. */
  docVersion: number;
}

// ---- Proposed changes a person approves (12 §12.4, 21 §21.4) -----------------------------------

/** One partner rate row by its key: destination, size (none: any size) and basis. */
export const partnerRateKeySchema = partnerRateRowBase
  .pick({ appliesTo: true, destination: true, sizeId: true, basis: true })
  .strict();

/**
 * Input to `proposePartnerRates` (Grok's back-office connection): rows to add or change and rows to
 * take out. Nothing changes until a person approves the proposal (`decideConfigProposal`).
 */
export const proposePartnerRatesInputSchema = z
  .object({
    partnerId: z.string().min(1).max(40),
    set: z.array(partnerRateRowSchema).max(2000).default([]),
    remove: z.array(partnerRateKeySchema).max(2000).default([]),
    /** Why: where the new rates come from (the partner's new tariff, a quote, …). */
    note: z.string().trim().min(3).max(500),
  })
  .strict()
  .refine((v) => v.set.length + v.remove.length > 0, { message: 'Nothing to change' });

export const CONFIG_PROPOSAL_STATUSES = [
  'pending',
  'approved',
  'rejected',
  'superseded',
  'stale',
] as const;

export const listConfigProposalsInputSchema = z
  .object({
    status: z.enum(['pending', 'all']).default('pending'),
    partnerId: z.string().min(1).max(40).optional(),
  })
  .strict();

export const decideConfigProposalInputSchema = z
  .object({
    proposalId: z.string().regex(/^cfp_[0-9A-HJKMNP-TV-Z]{26}$/),
    decision: z.enum(['approve', 'reject']),
    note: z.string().trim().max(300).default(''),
  })
  .strict();

/** Input to `publishRateCard`: copy a draft into a new active version and retire the previous one (D-09). */
export const publishRateCardInputSchema = z.object({ id: z.string().min(1).max(60) }).strict();
/** Input to `retireRateCard`: take a live card out of use without a replacement. */
export const retireRateCardInputSchema = z.object({ id: z.string().min(1).max(60) }).strict();

/** Input to `generateBarcodeBatch` (03 §3.4 barcode lifecycle). */
export const generateBarcodeBatchInputSchema = z
  .object({
    count: z.number().int().min(1).max(5000),
    issuedToBranchId: branchCodeSchema.optional(),
  })
  .strict();

/** Input to `importPerBoxCosts`: rows from the owner's spreadsheet (CSV parsed in the browser). */
export const importPerBoxCostsInputSchema = z
  .object({
    costModelId: z.string().min(1).max(60),
    rows: z.array(perBoxCostRowSchema).min(1).max(5000),
  })
  .strict();
